import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MergeIcon from "@mui/icons-material/Merge";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

const CompareDonorsDialog = ({
  open,
  onClose,
  sourceDonor,
  targetDonor,
  onMerge,
  loading = false,
}) => {
  const [mergedData, setMergedData] = useState({});
  const [customValues, setCustomValues] = useState({});

  // Fields to compare and merge
  const compareFields = [
    { key: "donorId", label: "Donor ID", editable: false },
    { key: "salutation", label: "Salutation" },
    { key: "initial", label: "Initial" },
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "address", label: "Address" },
    { key: "address1", label: "Address 1" },
    { key: "address2", label: "Address 2" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "country", label: "Country" },
    { key: "pincode", label: "Pincode" },
    { key: "panCardNo", label: "PAN Card No" },
    { key: "mobile1", label: "Mobile 1" },
    { key: "mobile2", label: "Mobile 2" },
    { key: "mailId", label: "Email ID" },
    { key: "status", label: "Status" },
    { key: "prasad", label: "Prasad", type: "boolean" },
    { key: "donorRemarks", label: "Donor Remarks" },
  ];

  useEffect(() => {
    if (sourceDonor && targetDonor) {
      // Initialize merged data with target donor values by default
      const initialMergedData = {};
      compareFields.forEach((field) => {
        initialMergedData[field.key] =
          targetDonor[field.key] !== undefined &&
          targetDonor[field.key] !== null
            ? targetDonor[field.key]
            : "";
      });
      setMergedData(initialMergedData);
    }
  }, [sourceDonor, targetDonor]);

  const handleFieldSelection = (fieldKey, value, source) => {
    if (source === "custom") {
      setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
      setMergedData((prev) => ({ ...prev, [fieldKey]: value }));
    } else {
      setMergedData((prev) => ({
        ...prev,
        [fieldKey]:
          source === "source" ? sourceDonor[fieldKey] : targetDonor[fieldKey],
      }));
      // Clear custom value if switching away from custom
      if (customValues[fieldKey]) {
        setCustomValues((prev) => {
          const newCustom = { ...prev };
          delete newCustom[fieldKey];
          return newCustom;
        });
      }
    }
  };

  const handleCustomValueChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
    setMergedData((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleMerge = () => {
    const mergePayload = {
      sourceDonorId: sourceDonor._id,
      targetDonorId: targetDonor._id,
      mergedData: mergedData,
    };
    console.log("Merging donors with payload:", mergePayload);
    onMerge(mergePayload);
  };

  const getFieldValue = (donor, field) => {
    const value = donor[field.key];
    if (field.type === "boolean") {
      return value === true ? "Yes" : value === false ? "No" : "Not Set";
    }
    return value || "Not Set";
  };

  const renderFieldComparison = (field) => {
    const sourceValue = getFieldValue(sourceDonor, field);
    const targetValue = getFieldValue(targetDonor, field);
    const isDifferent = sourceValue !== targetValue;
    const currentSelection =
      mergedData[field.key] === sourceDonor[field.key]
        ? "source"
        : mergedData[field.key] === targetDonor[field.key]
        ? "target"
        : "custom";

    return (
      <Card key={field.key} variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {field.label}
            {isDifferent && (
              <Chip
                label="Different"
                color="warning"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box p={2} bgcolor="#ffebee" borderRadius={1}>
                <Typography variant="subtitle2" color="error">
                  Source Donor (Will be deleted)
                </Typography>
                <Typography variant="body2">{sourceValue}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box p={2} bgcolor="#e8f5e8" borderRadius={1}>
                <Typography variant="subtitle2" color="success.main">
                  Target Donor (Will be kept)
                </Typography>
                <Typography variant="body2">{targetValue}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={currentSelection}
                  onChange={(e) =>
                    handleFieldSelection(
                      field.key,
                      e.target.value,
                      e.target.value
                    )
                  }
                >
                  <FormControlLabel
                    value="source"
                    control={<Radio />}
                    label="Use Source Value"
                    disabled={field.editable === false}
                  />
                  <FormControlLabel
                    value="target"
                    control={<Radio />}
                    label="Use Target Value"
                    disabled={field.editable === false}
                  />
                  {field.editable !== false && (
                    <FormControlLabel
                      value="custom"
                      control={<Radio />}
                      label="Custom Value"
                    />
                  )}
                </RadioGroup>

                {currentSelection === "custom" && field.type === "boolean" && (
                  <RadioGroup
                    row
                    value={customValues[field.key]}
                    onChange={(e) =>
                      handleCustomValueChange(
                        field.key,
                        e.target.value === "true"
                      )
                    }
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}

                {currentSelection === "custom" && field.type !== "boolean" && (
                  <TextField
                    size="small"
                    value={customValues[field.key] || ""}
                    onChange={(e) =>
                      handleCustomValueChange(field.key, e.target.value)
                    }
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (!sourceDonor || !targetDonor) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <div
        className="modal-header"
        style={{
          background: "#1976D2",
          color: "white",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <DialogTitle style={{ color: "white", padding: 0 }}>
          <CompareArrowsIcon sx={{ mr: 1 }} />
          Compare and Merge Donors
        </DialogTitle>
        <IconButton onClick={onClose} disabled={loading}>
          <CloseIcon sx={{ color: "white" }} />
        </IconButton>
      </div>

      <DialogContent sx={{ p: 3 }}>
        <Box mb={3}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Review the differences between donors and select which values to
            keep in the merged donor. The source donor will be deleted and all
            its donations will be transferred to the target donor.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Box p={2} bgcolor="#ffebee" borderRadius={1}>
                <Typography variant="h6" color="error">
                  Source Donor (Will be deleted)
                </Typography>
                <Typography variant="body2">
                  ID: {sourceDonor.donorId} | Name: {sourceDonor.firstName}{" "}
                  {sourceDonor.lastName}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box p={2} bgcolor="#e8f5e8" borderRadius={1}>
                <Typography variant="h6" color="success.main">
                  Target Donor (Will be kept)
                </Typography>
                <Typography variant="body2">
                  ID: {targetDonor.donorId} | Name: {targetDonor.firstName}{" "}
                  {targetDonor.lastName}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
          {compareFields.map((field) => renderFieldComparison(field))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #ddd" }}>
        <Button
          onClick={handleMerge}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <MergeIcon />}
          size="large"
        >
          {loading ? "Merging..." : "Merge Donors"}
        </Button>
        <Button
          onClick={onClose}
          color="error"
          variant="outlined"
          disabled={loading}
          size="large"
          sx={{ ml: 2 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareDonorsDialog;
